/* eslint-disable multiline-ternary */
import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Cookies from "js-cookie";
import { PlusIcon, ChevronLeftIcon } from "@heroicons/react/solid";

import ShareSelection from "./NominalShareSelection";
import SharesTable from "./NominalSharesTable";
import SharesCategoryTabs from "./SharesCategoryTabs";
import ViewShare from "./ViewShare";
import ModalWrapper from "components/lib/Shared/ModalWrapper";
import { getTakenShares, roundNumber } from "utils";
import ShareInformationTopSection from "components/lib/Global/ShareInformationTopSection";
import CommentsList from "components/lib/Global/CommentsList";
import ErrorBanner from "components/lib/Shared/ErrorBanner";
import Button from "components/lib/Shared/Button";
import { Constants } from "config/constants";
import { validateNominalSharesRestrictions } from "utils/nominalShares";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";
import { openModal } from "state/slices/modals";
import { updateNominalSharesSlice } from "state/slices/tables/nominalSharesTable";
import {
  submitAuthorizationsAsync,
  getShareAuthorizationsAsync,
  resetAuthorizationSharesTable,
} from "state/slices/shareCapitalAuthorization";
import {
  getShareAllotmentsAsync,
  setPreEmptionRights,
  applyProRataAsync,
  applyRightsIssueAsync,
} from "state/slices/shareCapitalAllotment";
import { getAllInitiateTransactionChangeAsync } from "state/slices/initiateTransactionChange";
import ProposedSharesTable from "./ProposedSharesTable";
import DateOfAuthorization from "./DateOfAuthorization";
import PreEmptionRights from "./PreEmptionRights";
import { useToast } from "hooks/useToast";
import UnallotedSharesContext from "context/UnallotedSharesContext";
import AddSharesOnOffer from "components/CompanyDashboard/MaintainYourCompany/Changes/ShareCapital/AllotmentOfShares/AllotmentForms/addSharesOnOfferForm";
import AllotmentForm from "components/CompanyDashboard/MaintainYourCompany/Changes/ShareCapital/AllotmentOfShares/AllotmentForms/allotmentForm";
import AllotmentResultsTable from "components/CompanyDashboard/MaintainYourCompany/Changes/ShareCapital/AllotmentOfShares/AllotmentResultsTable";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";

// ─────────────────────────────────────────────────────────────────────────────
// Small UI helpers
// ─────────────────────────────────────────────────────────────────────────────
const LoadingOverlay = ({ label = "Loading…" }) => (
  <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center rounded">
    <div className="flex items-center gap-2">
      <SpinnerIcon className="h-5 w-5 text-sflPrimary" />
      {label ? <span className="text-sm text-gray-600">{label}</span> : null}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

const SPECIAL_TYPES = [
  "authorization",
  "unallotedShares",
  "privateWithMoreThanOneClass",
];

const FORM_REQUIRED_TYPES = [
  "authorization",
  "unallotedShares",
  "privateWithMoreThanOneClass",
  "PVTwithOneClass",
  "existingAuth",
  "shareTransfer",
  "transmissionOfShares",
  "forfeitureOfShares",
  "surrenderOfShares",
  "sharesBuyback",
  "cancelationOfTreasuryShares",
  "saleTransferOfTreasuryShares",
  "sharesRedemption",
  "noticeToCourt",
  "takeoverOffer",
  "noticeOfChangeOfName",
  "noticeOfParticulars",
  "solvencyStatement",
  "courtOrder",
  "debenturesChargesRegistration",
  "debenturesChargesAmendment",
];

const SAVE_AND_PROCEED_TYPES = [
  "PVTwithOneClass",
  "shareTransfer",
  "transmissionOfShares",
  "forfeitureOfShares",
  "surrenderOfShares",
  "sharesBuyback",
  "cancelationOfTreasuryShares",
  "saleTransferOfTreasuryShares",
  "sharesRedemption",
  "sharesConsolidation",
  "sharesSubdivision",
  "noticeToCourt",
  "takeoverOffer",
  "noticeOfChangeOfName",
  "noticeOfParticulars",
  "solvencyStatement",
  "courtOrder",
  "debenturesChargesRegistration",
  "debenturesChargesAmendment",
];

const processTypeToChangeType = {
  authorization: "AUTHORIZATION",
  privateWithMoreThanOneClass: "AUTHORIZATION-AND-ALLOTMENT",
  unallotedShares: "ALLOTMENT",
  PVTwithOneClass: "ALLOTMENT",
  existingAuth: "ALLOTMENT",
  shareTransfer: "SHARE-TRANSFER",
  transmissionOfShares: "TRANSMISSION-OF-SHARES",
  forfeitureOfShares: "FORFEITURE-OF-SHARES",
  surrenderOfShares: "SURRENDER-OF-SHARES",
  sharesBuyback: "SHARES-BUYBACK",
  cancelationOfTreasuryShares: "CANCELATION-OF-TREASURY-SHARES",
  saleTransferOfTreasuryShares: "SALE-TRANSFER-OF-TREASURY-SHARES",
  sharesRedemption: "SHARES-REDEMPTION",
  sharesConsolidation: "SHARES-CONSOLIDATION",
  sharesSubdivision: "SHARES-SUBDIVISION",
  noticeToCourt: "NOTICE-TO-COURT",
  takeoverOffer: "TAKEOVER-OFFER",
  noticeOfChangeOfName: "NOTICE-OF-CHANGE-OF-NAME",
  noticeOfParticulars: "NOTICE-OF-PARTICULARS",
  solvencyStatement: "SOLVENCY-STATEMENT",
  courtOrder: "COURT-ORDER",
  debenturesChargesRegistration: "DEBENTURES-CHARGES-REGISTRATION",
  debenturesChargesAmendment: "DEBENTURES-CHARGES-AMENDMENT",
};

const NominalShareCapitalTab = forwardRef((props, ref) => {
  const {
    handleBackBtn,
    handleGoForward,
    handleGoBack,
    handleNextBtn,
    submitBtnLabel,
    isSubmitting,
  } = props;
  const { selectionsMade, createdTransactionsIds, setCreatedTransactionsIds } =
    useContext(UpdateCompanyDetailsContext);

  const role = Cookies.get("role");
  const { toast } = useToast(5000);
  const { isInChangesModule } = useContext(UnallotedSharesContext);
  const isInManualUpdate = props?.isInManualUpdate;
  const dispatch = useDispatch();

  const entityType = props?.entityType;
  const companyId = props?.company;
  const companyOfficialsSlice =
    useSelector((state) => state.companyOfficialsSlice) || {};
  const officials = companyOfficialsSlice.companyOfficials?.items || [];
  

  const filteredOfficials = officials.filter((official) =>
    [
      Constants.APPLICANT_CAPACITIES.SHAREHOLDER,
      Constants.APPLICANT_CAPACITIES.JOINT_SHAREHOLDER,
      Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER,
      Constants.APPLICANT_CAPACITIES.JOINT_SHAREHOLDER,
    ].includes(official.designation)
  );




  const shares = useSelector((s) => s.sharesTableSlice.shares);
  const shareAllotments = useSelector(
    (s) => s.shareAllotmentSlice?.allotments || []
  );
  const preEmptionRights = useSelector(
    (s) => s.shareAllotmentSlice.preEmptionRights
  );
  const shareAuthorizationSlice = useSelector((s) => s.shareAuthorizationSlice);
  const { initiatedTransactions } = useSelector(
    (s) => s.initiatedTransactionsSlice
  );
  const { comments } = useSelector((s) => s.commentsSlice);

  const history = useHistory();
  const takenShares = getTakenShares(
    useSelector((state) => state.companyOfficialsSlice.companyOfficials.items)
  );

  const [showAddMoreSharesBtn, setShowAddMoreSharesBtn] = useState(true);
  const [mode, setMode] = useState({ editing: false, id: "" });
  const [dateFields, setDateFields] = useState({
    dateOfAuthorization: "",
    dateOfExpiry: "",
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [transactionCreated, setTransactionCreated] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [showAllotmentForm, setShowAllotmentForm] = useState(false);
  const [allotmentMode, setAllotmentMode] = useState("rightsIssue"); // "rightsIssue" or "proRata"
  const [rightsIssueProcessed, setRightsIssueProcessed] = useState(false);
  const [isOpeningForNextClass, setIsOpeningForNextClass] = useState(false);
  const [proRataProcessed, setProRataProcessed] = useState(false);
  const [isProRataSubmitting, setIsProRataSubmitting] = useState(false);
  const [isAllotManuallySubmitting, setIsAllotManuallySubmitting] =
    useState(false);
  const [
    isAddSpecificShareholderSubmitting,
    setIsAddSpecificShareholderSubmitting,
  ] = useState(false);
  const [formStepErrors, setFormStepErrors] = useState([]);
  const [showAdditionalSharesForm, setShowAdditionalSharesForm] =
    useState(false);
  const [activeAdditionalFormChangeType, setActiveAdditionalFormChangeType] =
    useState(null);
  const [excludedShareClasses, setExcludedShareClasses] = useState([]);
  const [submittedAllocations, setSubmittedAllocations] = useState([]);
  const [reorganizationData, setReorganizationData] = useState({});

  const handleSaveReorganization = (data) => {
    setReorganizationData((prev) => ({
      ...prev,
      [data.sharesCategory]: data,
    }));
  };

  // Ref to track if component is mounted to prevent state updates on unmounted component
  const isMountedRef = useRef(true);

  // NEW: global-ish loading flags
  const [isBootstrapping, setIsBootstrapping] = useState(false); // initial load of tables/txs
  const [isSyncingAfterUpdate, setIsSyncingAfterUpdate] = useState(false);

  // Derived composite "UI busy" flag for disabling actions
  const uiBusy =
    isBootstrapping ||
    isSyncingAfterUpdate ||
    isProRataSubmitting ||
    isAllotManuallySubmitting ||
    isAddSpecificShareholderSubmitting;

  // Derive selectedChangeType from selectionsMade
  const selectedChangeType = useMemo(() => {
    if (!selectionsMade?.shareInformation) return [];
    const {
      capitalIncrease,
      capitalReduction,
      shareholdingChanges,
      sharesManagement,
    } = selectionsMade.shareInformation;

    const allChangeTypes = [
      ...(capitalIncrease || []),
      ...(capitalReduction || []),
      ...(shareholdingChanges || []),
      ...(sharesManagement || []),
    ];

    const specialType = allChangeTypes.find((type) =>
      SPECIAL_TYPES.includes(type)
    );
    const selectedTypes = specialType ? [specialType] : allChangeTypes;
    return selectedTypes;
  }, [selectionsMade]);

  // Form wizard state (AddSharesOnOffer)
  const [pendingChangeTypes, setPendingChangeTypes] = useState([]);
  const [currentFormIndex, setCurrentFormIndex] = useState(0);

  const isChangeTypeSelected = (type) => selectedChangeType.includes(type);

  const primaryChangeType =
    selectedChangeType.length > 0 ? selectedChangeType[0] : "";

  // Cleanup on unmount to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const shareInformationComments = comments.filter(
    (c) => c.formName === "shareInformation" && !c.isResolved
  );

  const hasAnyAuth = shareAuthorizationSlice.totalCount > 0;
  const hasUnapprovedAuth = useMemo(
    () =>
      (shareAuthorizationSlice.shares || []).some(
        (a) => a.isApproved === false
      ),
    [shareAuthorizationSlice.shares]
  );
  const hasAnyAllot = shareAllotments.length > 0;

  // Check if we have any authorization (proposed/approved). If any exists, hide actions in NominalShares table.
  const hasProposedAuthorizations = useMemo(() => {
    return (shareAuthorizationSlice.shares || []).length > 0;
  }, [shareAuthorizationSlice.shares, isBootstrapping, isSyncingAfterUpdate]);

  // Check if shares on offer exist in transactions
  const hasSharesOnOffer = useMemo(() => {
    let processType = "unallotedShares";
    if (isChangeTypeSelected("privateWithMoreThanOneClass")) {
      processType = "privateWithMoreThanOneClass";
    } else if (isChangeTypeSelected("PVTwithOneClass")) {
      processType = "PVTwithOneClass";
    } else if (isChangeTypeSelected("existingAuth")) {
      processType = "existingAuth";
    }

    const targetChangeType = processTypeToChangeType[processType];

    return initiatedTransactions.items.some(
      (tx) =>
        tx.transactionType === Constants.TRANSACTION_TYPES.MANUAL &&
        tx.changeType === targetChangeType &&
        tx.processType === processType &&
        ((Array.isArray(tx.allotments) && tx.allotments.length > 0) ||
          (Array.isArray(tx.shareCapitalChanges) &&
            tx.shareCapitalChanges.length > 0))
    );
  }, [
    initiatedTransactions.items,
    selectedChangeType,
    isBootstrapping,
    isSyncingAfterUpdate,
  ]);

  // Initial load with explicit spinners
  useEffect(() => {
    (async () => {
      dispatch(resetAuthorizationSharesTable());
      setShowAddMoreSharesBtn(true);
      setDateFields({ dateOfAuthorization: "", dateOfExpiry: "" });
      setTransactionCreated(false);
      setTransactionId(null);
      setRightsIssueProcessed(false);
      setProRataProcessed(false);
      setSubmittedAllocations([]);

      if (!companyId) return;

      setIsBootstrapping(true);
      try {
        await Promise.all([
          dispatch(getShareAuthorizationsAsync(companyId)),
          dispatch(getShareAllotmentsAsync(companyId)),
          dispatch(
            getAllInitiateTransactionChangeAsync({
              companyId,
              params: {
                transactionType: Constants.TRANSACTION_TYPES.MANUAL,
                transactionIds: createdTransactionsIds,
              },
            })
          ),
        ]);
      } finally {
        setIsBootstrapping(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, dispatch, isInManualUpdate]);

  // Ensure the correct view is shown when navigating back to this tab
  useEffect(() => {
    let processType = "unallotedShares";
    if (isChangeTypeSelected("privateWithMoreThanOneClass")) {
      processType = "privateWithMoreThanOneClass";
    } else if (isChangeTypeSelected("PVTwithOneClass")) {
      processType = "PVTwithOneClass";
    } else if (isChangeTypeSelected("existingAuth")) {
      processType = "existingAuth";
    }
    const tx = (initiatedTransactions.items || []).find(
      (t) =>
        t.transactionType === Constants.TRANSACTION_TYPES.MANUAL &&
        t.processType === processType
    );
    const hasRightsIssue = tx?.shareCapitalChanges?.some(
      (c) => c.option === "rightsIssue"
    );
    const hasProRata = tx?.shareCapitalChanges?.some(
      (c) => c.option === "proRata"
    );
    const hasManual = tx?.shareCapitalChanges?.some(
      (c) => c.option === "manual" || c.option === "specificShareholderOffer"
    );

    if (hasRightsIssue) {
      const allocations = getSubmittedAllocations(processType, "rightsIssue");
      setAllotmentMode("rightsIssue");
      setRightsIssueProcessed(allocations.length > 0);
      setSubmittedAllocations(allocations);
      // Keep form open during iterative processing - only close when manually closed or all done
      // Don't automatically close the form based on allocations existing
    } else if (hasProRata) {
      const allocations = getSubmittedAllocations(processType, "proRata");
      setAllotmentMode("proRata");
      setProRataProcessed(allocations.length > 0);
      setSubmittedAllocations(allocations);
      // Keep form open during iterative processing - only close when manually closed or all done
      // Don't automatically close the form based on allocations existing
    } else if (hasManual) {
      // Check if it's manual or specificShareholderOffer
      const hasManualOption = tx?.shareCapitalChanges?.some(
        (c) => c.option === "manual"
      );
      const hasSpecificShareholderOption = tx?.shareCapitalChanges?.some(
        (c) => c.option === "specificShareholderOffer"
      );

      if (hasManualOption) {
        const allocations = getSubmittedAllocations(processType, "manual");
        setAllotmentMode("manual");
        setSubmittedAllocations(allocations);
      } else if (hasSpecificShareholderOption) {
        const allocations = getSubmittedAllocations(
          processType,
          "specificShareholderOffer"
        );
        setAllotmentMode("specificShareholderOffer");
        setSubmittedAllocations(allocations);
      }

      setShowAllotmentForm(false);
      setRightsIssueProcessed(false);
      setProRataProcessed(false);
    }
  }, [initiatedTransactions.items, selectedChangeType, refreshTrigger]);

  // Simple auto-closing logic: only close when form was just used for successful submission
  // We'll handle this directly in the transaction update functions instead of complex useEffect

  // If an approved auth already has dates, keep in view mode
  useEffect(() => {
    if (isInManualUpdate && shareAuthorizationSlice.shares.length > 0) {
      const authorization = shareAuthorizationSlice.shares.find(
        (auth) => auth.isApproved === true
      );
      if (authorization?.authorizationDate && authorization?.expirationDate) {
        setDateFields({
          dateOfAuthorization: authorization.authorizationDate,
          dateOfExpiry: authorization.expirationDate,
        });
      }
    }
  }, [isInManualUpdate, shareAuthorizationSlice.shares]);

  const validateTab = (goToNextStep = false) => {
    const { errors } = validateNominalSharesRestrictions({ shares });
    setFormStepErrors(errors);
    if (errors.length === 0 && goToNextStep) {
      handleNextBtn?.();
    }
  };

  const handleAuthorizationTransactionSubmit = async () => {
    try {
      const submissionData = {
        companyId,
        dateFields,
        transactionType: Constants.TRANSACTION_TYPES.MANUAL,
      };
      if (isChangeTypeSelected("privateWithMoreThanOneClass")) {
        submissionData.changeType = "AUTHORIZATION-AND-ALLOTMENT";
        submissionData.processType = "privateWithMoreThanOneClass";
        submissionData.isApproved = true;
      } else if (isChangeTypeSelected("authorization")) {
        submissionData.changeType = "AUTHORIZATION";
        submissionData.processType = "authorization";
        submissionData.isApproved = false;
      }

      const resultAction = await dispatch(
        submitAuthorizationsAsync(submissionData)
      );

      if (submitAuthorizationsAsync.fulfilled.match(resultAction)) {
        const response = resultAction.payload;
        setCreatedTransactionsIds([
          ...createdTransactionsIds,
          response.data.transaction._id,
        ]);

        const message =
          response?.data?.message || "Authorization submitted successfully";
        toast("success", message);
        setTransactionCreated(true);
        if (isChangeTypeSelected("privateWithMoreThanOneClass")) {
          const transaction = initiatedTransactions.items.find(
            (tx) =>
              tx.transactionType === Constants.TRANSACTION_TYPES.MANUAL &&
              tx.changeType === "AUTHORIZATION-AND-ALLOTMENT" &&
              tx.processType === "privateWithMoreThanOneClass"
          );
          if (transaction) {
            setTransactionId(response.data.transaction._id);
          }
          await dispatch(getShareAuthorizationsAsync(companyId));
        }
        setRefreshTrigger((prev) => prev + 1);
        return true;
      }

      const errorPayload = resultAction.payload;
      toast(
        "error",
        errorPayload?.message ||
          (typeof errorPayload === "string"
            ? errorPayload
            : "Failed to submit authorizations. Please try again.")
      );
      return false;
    } catch (error) {
      toast(
        "error",
        error?.message ||
          "An unexpected error occurred during submission. Please try again."
      );
      return false;
    }
  };

  useImperativeHandle(ref, () => ({
    submit: handleAuthorizationTransactionSubmit,
  }));

  const updateExistingTransaction = async (
    allocations,
    remainder,
    responsePayload,
    changeType
  ) => {
    if (isMountedRef.current) {
      setIsSyncingAfterUpdate(true);
    }
    try {
      // Refresh transaction and update it
      const fetchAction = await dispatch(
        getAllInitiateTransactionChangeAsync({
          companyId,
          params: {
            transactionType: Constants.TRANSACTION_TYPES.MANUAL,
            transactionIds: createdTransactionsIds,
            processType: changeType,
          },
        })
      );

      const txs = fetchAction?.payload?.items || [];
      const target = txs.find((tx) => tx.processType === changeType);
      if (!target?._id) {
        throw new Error(`No existing '${changeType}' transaction found.`);
      }
      // Find existing change entry more specifically - match by classOfShares and sharesOnOffer
      const shareCapitalChange = target.shareCapitalChanges.find(
        (change) =>
          change.classOfShares === responsePayload.classOfShares &&
          change.sharesOnOffer === responsePayload.sharesOnOffer &&
          (responsePayload.AuthorizationId
            ? change.AuthorizationId?.toString() ===
              responsePayload.AuthorizationId
            : true)
      );

      const baseChange = shareCapitalChange || {
        changeType: changeType,
        classOfShares: responsePayload.classOfShares,
        sharesOnOffer: responsePayload.sharesOnOffer,
        offerPricePerShare: responsePayload.offerPricePerShare,
        premiumPerShare: responsePayload.premiumPerShare,
        dateOfOffer: responsePayload.dateOfOffer,
        AuthorizationId: responsePayload.AuthorizationId,
      };

      const newChangeEntry = {
        ...baseChange,
        allocations,
        remainder,
        option: responsePayload.option,
        forEvery: responsePayload.forEvery,
        allotAmount: responsePayload.allotAmount,
        AmountCreditedAsPaidUpCapitalPerShare: Number(
          responsePayload.AmountCreditedAsPaidUpCapitalPerShare || 0
        ),
      };

      // Filter out the existing entry more specifically to avoid removing wrong entries
      const updatedShareCapitalChanges = [
        ...target.shareCapitalChanges.filter(
          (change) =>
            !(
              change.classOfShares === responsePayload.classOfShares &&
              change.sharesOnOffer === responsePayload.sharesOnOffer &&
              (responsePayload.AuthorizationId
                ? change.AuthorizationId?.toString() ===
                  responsePayload.AuthorizationId
                : true)
            )
        ),
        newChangeEntry,
      ];

      const updatePayload = {
        companyId,
        documentId: target?._id,
        formName: "initiateTransactionChange",
        shareCapitalChanges: updatedShareCapitalChanges,
      };

      const txUpdate = await handleCompanyMaintenanceFormData(
        "existing-request",
        updatePayload,
        companyId
      );
      if (
        txUpdate.type !==
        "transactionChange/editInitiatedTransactionChangeAsync/fulfilled"
      ) {
        const msg =
          txUpdate?.payload?.messageText ||
          "Failed to update transaction with allocations.";
        throw new Error(msg);
      }

      // Pull fresh tx state after update so UI can decide next buttons/tables
      await dispatch(
        getAllInitiateTransactionChangeAsync({
          companyId,
          params: {
            transactionType: Constants.TRANSACTION_TYPES.MANUAL,
            transactionIds: createdTransactionsIds,
          },
        })
      );
      return txUpdate.payload;
    } catch (error) {
      toast(
        "error",
        error?.message || "Failed to update transaction with allocations."
      );
      throw error;
    } finally {
      if (isMountedRef.current) {
        setIsSyncingAfterUpdate(false);
      }
    }
  };

  // Helper function to get transaction allotment data
  const getTransactionAllotmentData = (processType) => {
    const targetChangeType = processTypeToChangeType[processType];
    const transaction = initiatedTransactions.items.find(
      (tx) =>
        tx.transactionType === Constants.TRANSACTION_TYPES.MANUAL &&
        tx.changeType === targetChangeType &&
        tx.processType === processType
    );

    if (!transaction) return null;

    // Extract allotment data from shareCapitalChanges
    if (transaction.shareCapitalChanges?.length > 0) {
      return transaction.shareCapitalChanges.map((change) => ({
        classOfShares: change.classOfShares,
        sharesOnOffer: change.sharesOnOffer,
        offerPricePerShare: change.offerPricePerShare,
        premiumPerShare: change.premiumPerShare,
        dateOfOffer: change.dateOfOffer,
        AuthorizationId: change.AuthorizationId,
        nominalShareId: change.nominalShareId,
        _id: change._id || transaction._id,
      }));
    }

    // Extract from allotmentsPopulated if available
    if (transaction.allotmentsPopulated?.length > 0) {
      return transaction.allotmentsPopulated;
    }

    return [];
  };

  // Helper function to get submitted allocations from transactions
  const getSubmittedAllocations = (processType, mode) => {
    const targetChangeType = processTypeToChangeType[processType];
    const transaction = initiatedTransactions.items.find(
      (tx) =>
        tx.transactionType === Constants.TRANSACTION_TYPES.MANUAL &&
        tx.changeType === targetChangeType &&
        tx.processType === processType &&
        tx.status === Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT
    );

    if (!transaction?.shareCapitalChanges) return [];

    return transaction.shareCapitalChanges
      .filter((change) => change.option === mode)
      .map((change) => ({
        classOfShares: change.classOfShares,
        sharesOnOffer: change.sharesOnOffer,
        AmountCreditedAsPaidUpCapitalPerShare:
          change.AmountCreditedAsPaidUpCapitalPerShare,
        allocations: change.allocations || [],
        remainder: change.remainder || 0,
        option: change.option,
        forEvery: change.forEvery,
        allotAmount: change.allotAmount,
        distributionTargetClasses: change.distributionTargetClasses || [],
      }));
  };

  // === NEW: persistent delete of an allocation ===
  const deleteAllocationFromTransaction = async ({
    classOfShares,
    sharesOnOffer,
    option,
  }) => {
    if (isMountedRef.current) {
      setIsSyncingAfterUpdate(true);
    }
    try {
      let processType = "unallotedShares";
      if (selectedChangeType.includes("privateWithMoreThanOneClass"))
        processType = "privateWithMoreThanOneClass";
      else if (selectedChangeType.includes("PVTwithOneClass"))
        processType = "PVTwithOneClass";

      const fetchAction = await dispatch(
        getAllInitiateTransactionChangeAsync({
          companyId,
          params: {
            transactionType: Constants.TRANSACTION_TYPES.MANUAL,
            transactionIds: createdTransactionsIds,
            processType,
          },
        })
      );

      const txs = fetchAction?.payload?.items || [];
      const target = txs.find((tx) => tx.processType === processType);
      if (!target?._id)
        throw new Error(`No existing '${processType}' transaction found.`);

      const changeToUpdate = target.shareCapitalChanges.find(
        (change) =>
          change.classOfShares === classOfShares &&
          Number(change.sharesOnOffer) === Number(sharesOnOffer) &&
          change.option === option
      );
      if (!changeToUpdate)
        throw new Error("No matching change entry found to delete.");

      // Update only the specified fields, including removing option
      const updatedChange = {
        ...changeToUpdate,
        allocations: [],
        remainder: changeToUpdate.sharesOnOffer || 0,
        AmountCreditedAsPaidUpCapitalPerShare: undefined,
        option: undefined,
      };

      // Replace the original change with the updated one
      const updatedShareCapitalChanges = target.shareCapitalChanges.map(
        (change) =>
          change.classOfShares === classOfShares &&
          Number(change.sharesOnOffer) === Number(sharesOnOffer) &&
          change.option === option
            ? updatedChange
            : change
      );

      const updatePayload = {
        companyId,
        documentId: target._id,
        formName: "initiateTransactionChange",
        shareCapitalChanges: updatedShareCapitalChanges,
      };

      const txUpdate = await handleCompanyMaintenanceFormData(
        "existing-request",
        updatePayload,
        companyId
      );

      if (
        txUpdate.type !==
        "transactionChange/editInitiatedTransactionChangeAsync/fulfilled"
      ) {
        const msg =
          txUpdate?.payload?.messageText || "Failed to delete allocation.";
        throw new Error(msg);
      }

      await dispatch(
        getAllInitiateTransactionChangeAsync({
          companyId,
          params: {
            transactionType: Constants.TRANSACTION_TYPES.MANUAL,
            transactionIds: createdTransactionsIds,
          },
        })
      );

      if (isMountedRef.current) {
        setSubmittedAllocations((prev) =>
          (prev || []).filter(
            (a) =>
              !(
                a.classOfShares === classOfShares &&
                Number(a.sharesOnOffer) === Number(sharesOnOffer) &&
                a.option === option
              )
          )
        );

        setRefreshTrigger((prev) => prev + 1); // Trigger UI refresh
      }
      toast("success", "Allocation deleted successfully.");
    } catch (e) {
      toast("error", e?.message || "Failed to delete allocation.");
    } finally {
      if (isMountedRef.current) {
        setIsSyncingAfterUpdate(false);
      }
    }
  };

  const updateRightsIssueTransaction = async (
    payload,
    { setSubmitting, setSubmittedPayload }
  ) => {
    try {
      setSubmitting(true);
      setIsSyncingAfterUpdate(true);

      // Determine the process type
      let processType = "unallotedShares";
      if (isChangeTypeSelected("privateWithMoreThanOneClass")) {
        processType = "privateWithMoreThanOneClass";
      } else if (isChangeTypeSelected("PVTwithOneClass")) {
        processType = "PVTwithOneClass";
      } else if (isChangeTypeSelected("existingAuth")) {
        processType = "existingAuth";
      }

      // Get transaction allotment data and filter for the specific share class being processed
      const transactionAllotments = getTransactionAllotmentData(processType);
      if (!transactionAllotments || transactionAllotments.length === 0) {
        throw new Error("No transaction allotment data found");
      }

      // Filter to only process the specific share class from the form
      const targetAllotment = transactionAllotments.find(
        (allotment) =>
          allotment.classOfShares === payload.shareCategory &&
          allotment.sharesOnOffer === Number(payload.sharesOnOffer)
      );

      if (!targetAllotment) {
        throw new Error(
          `Share class ${payload.shareCategory} with ${payload.sharesOnOffer} shares not found in transaction allotments`
        );
      }

      // Calculate cumulativeSharesOnOffer from all shareCapitalChanges
      const cumulativeSharesOnOffer = transactionAllotments.reduce(
        (sum, allotment) => {
          return sum + (Number(allotment.sharesOnOffer) || 0);
        },
        0
      );

      const responsePayload = await dispatch(
        applyRightsIssueAsync({
          shareAllotments: [targetAllotment], // Only process this specific share class
          filteredOfficials,
          option: "rightsIssue",
          companyId,
          forEvery: Number(payload.forEvery),
          allotAmount: Number(payload.allotAmount),
          distributionTargetClasses: payload.distributionTargetClasses || [],
          AmountCreditedAsPaidUpCapitalPerShare: Number(
            payload.paidUpShares || 0
          ),
          cumulativeSharesOnOffer,
        })
      );

      if (applyRightsIssueAsync.fulfilled.match(responsePayload)) {
        const resp = responsePayload.payload;
        const { allocations, remainder, option, forEvery, allotAmount } = resp;

        await updateExistingTransaction(
          allocations,
          remainder,
          {
            changeType: processType,
            classOfShares: resp.classOfShares,
            sharesOnOffer: resp.sharesOnOffer,
            offerPricePerShare: resp.offerPricePerShare,
            premiumPerShare: resp.premiumPerShare,
            dateOfOffer: resp.dateOfOffer,
            AuthorizationId: resp.AuthorizationId,
            option,
            forEvery,
            allotAmount,
            AmountCreditedAsPaidUpCapitalPerShare: Number(
              payload.paidUpShares || 0
            ),
            allocations,
            remainder,
          },
          processType
        );
        // Create the new allocation data immediately
        const newAllocation = {
          classOfShares: resp.classOfShares,
          sharesOnOffer: resp.sharesOnOffer,
          AmountCreditedAsPaidUpCapitalPerShare: Number(
            payload.paidUpShares || 0
          ),
          allocations: resp.allocations,
          remainder: resp.remainder,
          option: resp.option,
          forEvery: resp.forEvery,
          allotAmount: resp.allotAmount,
          distributionTargetClasses: payload.distributionTargetClasses || [],
        };

        // Update submittedAllocations immediately to show results table faster

        setSubmittedAllocations((prev) => {
          const existing = prev.find(
            (a) =>
              a.classOfShares === newAllocation.classOfShares &&
              a.sharesOnOffer === newAllocation.sharesOnOffer &&
              a.option === newAllocation.option
          );
          if (existing) {
            // Update existing allocation
            const updated = prev.map((a) =>
              a.classOfShares === newAllocation.classOfShares &&
              a.sharesOnOffer === newAllocation.sharesOnOffer &&
              a.option === newAllocation.option
                ? newAllocation
                : a
            );
            return updated;
          } else {
            // Add new allocation
            const updated = [...prev, newAllocation];
            return updated;
          }
        });

        setRightsIssueProcessed(true); // Mark as processed after each allocation
        setRefreshTrigger((prev) => prev + 1);

        // Close the form after successful submission with reduced delay
        if (!isOpeningForNextClass) {
          setTimeout(() => {
            if (isMountedRef.current) {
              setShowAllotmentForm(false);
            }
          }, 200); // Reduced delay for faster UI response
        } else {
        }

        // Return the allocation data for the results table
        return newAllocation;
      } else {
        const errorPayload = responsePayload.payload;
        toast(
          "error",
          errorPayload?.message ||
            "Failed to process rights issue. Please try again."
        );
      }
    } catch (error) {
      toast(
        "error",
        error?.message ||
          "An unexpected error occurred during rights issue processing."
      );
    } finally {
      setSubmitting(false);
      setIsSyncingAfterUpdate(false);
    }
  };

  const updateProRataTransaction = async (
    payload,
    { setSubmitting, setSubmittedPayload }
  ) => {
    try {
      setSubmitting(true);
      setIsSyncingAfterUpdate(true);

      // Determine the process type
      let processType = "unallotedShares";
      if (isChangeTypeSelected("privateWithMoreThanOneClass")) {
        processType = "privateWithMoreThanOneClass";
      } else if (isChangeTypeSelected("PVTwithOneClass")) {
        processType = "PVTwithOneClass";
      } else if (isChangeTypeSelected("existingAuth")) {
        processType = "existingAuth";
      }

      // Get transaction allotment data
      const transactionAllotments = getTransactionAllotmentData(processType);
      if (!transactionAllotments || transactionAllotments.length === 0) {
        throw new Error("No transaction allotment data found");
      }

      // Filter to process specific share class and amount
      const targetAllotments = transactionAllotments.filter(
        (a) =>
          a.classOfShares === payload.shareCategory &&
          a.sharesOnOffer === Number(payload.sharesOnOffer)
      );

      if (targetAllotments.length === 0) {
        throw new Error(
          `Share class ${payload.shareCategory} with ${payload.sharesOnOffer} shares not found in transaction allotments`
        );
      }

      // Calculate cumulativeSharesOnOffer from all shareCapitalChanges
      const cumulativeSharesOnOffer = transactionAllotments.reduce(
        (sum, allotment) => {
          return sum + (Number(allotment.sharesOnOffer) || 0);
        },
        0
      );

      const responsePayload = await dispatch(
        applyProRataAsync({
          shareAllotments: targetAllotments,
          filteredOfficials,
          option: "proRata",
          companyId,
          distributionTargetClasses: payload.distributionTargetClasses || [],
          AmountCreditedAsPaidUpCapitalPerShare: Number(
            payload.paidUpShares || 0
          ),
          cumulativeSharesOnOffer,
        })
      );

      if (applyProRataAsync.fulfilled.match(responsePayload)) {
        const resp = responsePayload.payload;
        const { allocations, remainder, option } = resp;

        await updateExistingTransaction(
          allocations,
          remainder,
          {
            changeType: processType,
            classOfShares: resp.classOfShares,
            sharesOnOffer: resp.sharesOnOffer,
            offerPricePerShare: resp.offerPricePerShare,
            premiumPerShare: resp.premiumPerShare,
            dateOfOffer: resp.dateOfOffer,
            AuthorizationId: resp.AuthorizationId,
            option,
            AmountCreditedAsPaidUpCapitalPerShare: Number(
              payload.paidUpShares || 0
            ),
            allocations,
          },
          processType
        );

        // Create the new allocation data immediately
        const newAllocation = {
          classOfShares: resp.classOfShares,
          sharesOnOffer: resp.sharesOnOffer,
          AmountCreditedAsPaidUpCapitalPerShare: Number(
            payload.paidUpShares || 0
          ),
          allocations, // Updated allocations
          remainder: resp.remainder,
          option: resp.option,
          distributionTargetClasses: payload.distributionTargetClasses || [],
        };

        // Update submittedAllocations immediately to show results table faster

        setSubmittedAllocations((prev) => {
          const existing = prev.find(
            (a) =>
              a.classOfShares === newAllocation.classOfShares &&
              a.sharesOnOffer === newAllocation.sharesOnOffer &&
              a.option === newAllocation.option
          );
          if (existing) {
            // Update existing allocation
            const updated = prev.map((a) =>
              a.classOfShares === newAllocation.classOfShares &&
              a.sharesOnOffer === newAllocation.sharesOnOffer &&
              a.option === newAllocation.option
                ? newAllocation
                : a
            );

            return updated;
          } else {
            // Add new allocation
            const updated = [...prev, newAllocation];

            return updated;
          }
        });

        setProRataProcessed(true); // Mark as processed after each allocation
        setRefreshTrigger((prev) => prev + 1);

        // Close the form after successful submission with reduced delay
        if (!isOpeningForNextClass) {
          setTimeout(() => {
            if (isMountedRef.current) {
              setShowAllotmentForm(false);
            }
          }, 200); // Reduced delay for faster UI response
        } else {
        }

        // Return the allocation data for the results table
        return newAllocation;
      } else {
        const errorPayload = responsePayload.payload;
        toast(
          "error",
          errorPayload?.message ||
            "Failed to process pro rata allocation. Please try again."
        );
      }
    } catch (error) {
      toast(
        "error",
        error?.message ||
          "An unexpected error occurred during pro rata processing."
      );
    } finally {
      setSubmitting(false);
      setIsSyncingAfterUpdate(false);
    }
  };

  const handleAllocateSharesProRataClick = () => {
    let processType = "unallotedShares";
    if (selectedChangeType.includes("privateWithMoreThanOneClass"))
      processType = "privateWithMoreThanOneClass";
    else if (selectedChangeType.includes("PVTwithOneClass"))
      processType = "PVTwithOneClass";
    else if (selectedChangeType.includes("existingAuth"))
      processType = "existingAuth";

    const existingAllocations = getSubmittedAllocations(processType, "proRata");
    setAllotmentMode("proRata");
    setProRataProcessed(existingAllocations.length > 0);
    setSubmittedAllocations(existingAllocations);
    // Set flag when initially opening the form
    setIsOpeningForNextClass(true);
    setShowAllotmentForm(true);

    // Reset flag after delay for initial opening
    setTimeout(() => {
      if (isMountedRef.current) {
        setIsOpeningForNextClass(false);
      }
    }, 1000);
  };
  const handleAllotManuallyClick = async () => {
    try {
      setIsAllotManuallySubmitting(true);

      // Determine the process type
      let processType = "unallotedShares";
      if (isChangeTypeSelected("privateWithMoreThanOneClass")) {
        processType = "privateWithMoreThanOneClass";
      } else if (isChangeTypeSelected("PVTwithOneClass")) {
        processType = "PVTwithOneClass";
      } else if (isChangeTypeSelected("existingAuth")) {
        processType = "existingAuth";
      }

      // Get transaction allotment data instead of shareAllotments slice
      const transactionAllotments = getTransactionAllotmentData(processType);
      if (!transactionAllotments || transactionAllotments.length === 0) {
        throw new Error("No transaction allotment data found");
      }

      // Process all allotments with manual option instead of just the first one
      const manualAllocations = [];
      for (const allotment of transactionAllotments) {
        const manualAllocation = {
          classOfShares: allotment.classOfShares,
          sharesOnOffer: allotment.sharesOnOffer,
          AmountCreditedAsPaidUpCapitalPerShare: 0, // Default, will be set manually
          allocations: [], // Empty for manual allocation
          remainder: allotment.sharesOnOffer || 0, // All shares remain unallocated for manual processing
          option: "manual",
          distributionTargetClasses: [],
        };
        manualAllocations.push(manualAllocation);

        // Update each transaction with manual option
        const manualResponsePayload = {
          option: "manual",
          classOfShares: allotment.classOfShares,
          sharesOnOffer: allotment.sharesOnOffer,
          offerPricePerShare: allotment.offerPricePerShare,
          premiumPerShare: allotment.premiumPerShare || 0,
          dateOfOffer: allotment.dateOfOffer,
          AuthorizationId: allotment.AuthorizationId,
        };

        await updateExistingTransaction(
          [], // Empty allocations for manual
          allotment.sharesOnOffer || 0, // All shares as remainder
          manualResponsePayload,
          processType
        );
      }

      // Set up the state for manual allocation display
      setAllotmentMode("manual");
      setSubmittedAllocations(manualAllocations);
      setRightsIssueProcessed(false);
      setProRataProcessed(false);

      // Add transaction IDs to context (similar to handleMoveForward)
      const targetChangeType = processTypeToChangeType[processType];
      const relevantTransactions = initiatedTransactions.items.filter(
        (tx) =>
          tx.transactionType === Constants.TRANSACTION_TYPES.MANUAL &&
          tx.changeType === targetChangeType &&
          tx.processType === processType
      );

      // Extract transaction IDs
      const transactionIds = relevantTransactions.map((tx) => tx._id);

      setCreatedTransactionsIds([...createdTransactionsIds, transactionIds]);

      // Navigate forward since manual allocations are handled in the next step
      handleGoForward();
    } catch (error) {
      toast(
        "error",
        error?.message || "Failed to set manual allocation option."
      );
    } finally {
      setIsAllotManuallySubmitting(false);
    }
  };

  const handleRightsIssueClick = () => {
    let processType = "unallotedShares";
    if (isChangeTypeSelected("privateWithMoreThanOneClass")) {
      processType = "privateWithMoreThanOneClass";
    } else if (isChangeTypeSelected("PVTwithOneClass")) {
      processType = "PVTwithOneClass";
    } else if (isChangeTypeSelected("existingAuth")) {
      processType = "existingAuth";
    }

    const existingAllocations = getSubmittedAllocations(
      processType,
      "rightsIssue"
    );
    setAllotmentMode("rightsIssue");
    setRightsIssueProcessed(existingAllocations.length > 0);
    setSubmittedAllocations(existingAllocations);
    // Set flag when initially opening the form
    setIsOpeningForNextClass(true);
    setShowAllotmentForm(true);

    // Reset flag after delay for initial opening
    setTimeout(() => {
      if (isMountedRef.current) {
        setIsOpeningForNextClass(false);
      }
    }, 1000);
  };

  const handleAddSharesClick = async () => {
    if (
      isChangeTypeSelected("privateWithMoreThanOneClass") &&
      !transactionCreated &&
      dateFields.dateOfAuthorization &&
      dateFields.dateOfExpiry
    ) {
      const ok = await handleAuthorizationTransactionSubmit();
      if (!ok) return;
    }

    const typesRequiringForm = selectedChangeType.filter((type) =>
      FORM_REQUIRED_TYPES.includes(type)
    );
    if (typesRequiringForm.length > 0) {
      setPendingChangeTypes(typesRequiringForm);
      setCurrentFormIndex(0);
    } else {
      toast(
        "error",
        "Please select at least one change type that requires a form."
      );
    }
  };

  const hideAddSharesForm = () => {
    setPendingChangeTypes([]);
    setCurrentFormIndex(0);
  };

  const hideAdditionalSharesForm = () => {
    setShowAdditionalSharesForm(false);
    setActiveAdditionalFormChangeType(null);
    setExcludedShareClasses([]);
  };

  const handleAddAnotherShareOnOffer = (unproposedClasses, changeType) => {
    setExcludedShareClasses(unproposedClasses || []);
    setActiveAdditionalFormChangeType(changeType);
    setShowAdditionalSharesForm(true);
  };

  const handleSaveAndProceed = async () => {
    if (
      (isChangeTypeSelected("authorization") ||
        isChangeTypeSelected("privateWithMoreThanOneClass")) &&
      (!dateFields.dateOfAuthorization || !dateFields.dateOfExpiry)
    ) {
      toast(
        "error",
        "Both Date of Authorization and Date of Expiry are required."
      );
      return;
    }

    if (isChangeTypeSelected("authorization")) {
      const ok = await handleAuthorizationTransactionSubmit();
      if (!ok) return;
      handleGoForward();
    } else {
      validateTab(true);
    }
  };

  const handleMoveForward = async () => {
    let processType = "unallotedShares";
    if (isChangeTypeSelected("privateWithMoreThanOneClass")) {
      processType = "privateWithMoreThanOneClass";
    } else if (isChangeTypeSelected("PVTwithOneClass")) {
      processType = "PVTwithOneClass";
    } else if (isChangeTypeSelected("existingAuth")) {
      processType = "existingAuth";
    }

    const targetChangeType = processTypeToChangeType[processType];
    const relevantTransactions = initiatedTransactions.items.filter(
      (tx) =>
        tx.transactionType === Constants.TRANSACTION_TYPES.MANUAL &&
        tx.changeType === targetChangeType &&
        tx.processType === processType
    );

    // Extract transaction IDs (using _id field from your data structure)
    const transactionIds = relevantTransactions.map((tx) => tx._id);

    setCreatedTransactionsIds([...createdTransactionsIds, transactionIds]);
    handleGoForward();
  };

  const handleAddClassOfShares = () => {
    setShowAddMoreSharesBtn(false);
    dispatch(updateNominalSharesSlice({ activeTab: "" }));
  };

  const handleEdit = (row) => {
    dispatch(openModal({ modalName: "main_shares_modal_edit" }));
    dispatch(updateNominalSharesSlice({ activeTab: "" }));
    setMode({ editing: true, id: row._id });
  };

  const handleView = (row) => {
    dispatch(openModal({ modalName: "main_shares_modal_view" }));
    dispatch(updateNominalSharesSlice({ activeTab: "" }));
  };

  const handleAddAnotherAllotmentClass = (remainingShares) => {
    // Set flag to prevent auto-close when opening for next class
    setIsOpeningForNextClass(true);
    // Reset the processed state to allow form to open for next share class
    setRightsIssueProcessed(false);
    setProRataProcessed(false);
    // Open the allotment form with the remaining shares
    setShowAllotmentForm(true);

    // Reset the flag after a reasonable delay to avoid interference
    setTimeout(() => {
      if (isMountedRef.current) {
        setIsOpeningForNextClass(false);
      }
    }, 1000); // Give it more time
  };

  const handleAddSpecificShareholderClick = async () => {
    try {
      setIsAddSpecificShareholderSubmitting(true);

      // Determine the process type
      let processType = "unallotedShares";
      if (isChangeTypeSelected("privateWithMoreThanOneClass")) {
        processType = "privateWithMoreThanOneClass";
      } else if (isChangeTypeSelected("PVTwithOneClass")) {
        processType = "PVTwithOneClass";
      } else if (isChangeTypeSelected("existingAuth")) {
        processType = "existingAuth";
      }

      // Get transaction allotment data instead of shareAllotments slice
      const transactionAllotments = getTransactionAllotmentData(processType);
      if (!transactionAllotments || transactionAllotments.length === 0) {
        throw new Error("No transaction allotment data found");
      }

      // Process all allotments with specificShareholderOffer option instead of just the first one
      const specificShareholderAllocations = [];
      for (const allotment of transactionAllotments) {
        const specificAllocation = {
          classOfShares: allotment.classOfShares,
          sharesOnOffer: allotment.sharesOnOffer,
          AmountCreditedAsPaidUpCapitalPerShare: 0, // Default, will be set manually
          allocations: [], // Empty for specific shareholder allocation
          remainder: allotment.sharesOnOffer || 0, // All shares remain unallocated for specific allocation
          option: "specificShareholderOffer",
          distributionTargetClasses: [],
        };
        specificShareholderAllocations.push(specificAllocation);

        // Update each transaction with specificShareholderOffer option
        const specificResponsePayload = {
          option: "specificShareholderOffer",
          classOfShares: allotment.classOfShares,
          sharesOnOffer: allotment.sharesOnOffer,
          offerPricePerShare: allotment.offerPricePerShare,
          premiumPerShare: allotment.premiumPerShare || 0,
          dateOfOffer: allotment.dateOfOffer,
          AuthorizationId: allotment.AuthorizationId,
        };

        await updateExistingTransaction(
          [], // Empty allocations for specific shareholder offer
          allotment.sharesOnOffer || 0, // All shares as remainder
          specificResponsePayload,
          processType
        );
      }

      // Set up the state for specific shareholder allocation display
      setAllotmentMode("specificShareholderOffer");
      setSubmittedAllocations(specificShareholderAllocations);
      setRightsIssueProcessed(false);
      setProRataProcessed(false);

      // Add transaction IDs to context (similar to handleMoveForward)
      const targetChangeType = processTypeToChangeType[processType];
      const relevantTransactions = initiatedTransactions.items.filter(
        (tx) =>
          tx.transactionType === Constants.TRANSACTION_TYPES.MANUAL &&
          tx.changeType === targetChangeType &&
          tx.processType === processType
      );

      // Extract transaction IDs
      const transactionIds = relevantTransactions.map((tx) => tx._id);

      setCreatedTransactionsIds([...createdTransactionsIds, transactionIds]);

      // Navigate forward since specific shareholder allocations are handled in the next step
      handleGoForward();
    } catch (error) {
      toast(
        "error",
        error?.message ||
          "Failed to set specific shareholder allocation option."
      );
    } finally {
      setIsAddSpecificShareholderSubmitting(false);
    }
  };

  const renderBottomButtons = () => {
    if (
      !showAddMoreSharesBtn ||
      pendingChangeTypes.length > 0 ||
      showAllotmentForm
    ) {
      return null;
    }

    // While deciding (or syncing) what to render down here, keep UI stable
    if (uiBusy) {
      return (
        <div className="flex items-center gap-2">
          <SpinnerIcon className="h-5 w-5 text-sflPrimary" />
          <span className="text-sm text-gray-600">Updating… Please wait</span>
        </div>
      );
    }

    // Authorization
    if (isChangeTypeSelected("authorization")) {
      if (!hasAnyAuth) return null;
      if (!dateFields.dateOfAuthorization || !dateFields.dateOfExpiry) {
        return null;
      }
      return (
        <Button
          onClick={handleSaveAndProceed}
          className="bg-sflPrimary px-2 py-3 rounded text-white h-12"
        >
          Save and Proceed
        </Button>
      );
    }

    // PrivateWithMoreThanOneClass
    if (isChangeTypeSelected("privateWithMoreThanOneClass")) {
      if (!hasAnyAuth) return null;
      if (!dateFields.dateOfAuthorization || !dateFields.dateOfExpiry) {
        return null;
      }

      if (
        !getHasProposedTable("privateWithMoreThanOneClass") &&
        hasUnapprovedAuth
      ) {
        return (
          <Button
            onClick={handleAddSharesClick}
            className="bg-[#31415d] px-2 py-3 rounded text-white h-12"
          >
            Add shares on offer
          </Button>
        );
      }
      if (!preEmptionRights) return null;

      if (
        (rightsIssueProcessed || proRataProcessed) &&
        submittedAllocations.length > 0
      ) {
        return (
          <Button
            onClick={handleMoveForward}
            className="bg-sflPrimary px-2 py-3 rounded text-white h-12"
          >
            Save and Proceed
          </Button>
        );
      }

      if (preEmptionRights === "applies") {
        return (
          <div className="flex gap-2">
            <Button
              onClick={handleAllocateSharesProRataClick}
              isLoading={isProRataSubmitting}
              loadingText="Allotting..."
              disabled={uiBusy}
              className="bg-[#31415d] px-2 py-3 rounded text-white h-12 disabled:opacity-70"
            >
              Allot Shares Pro Rata
            </Button>
            <Button
              onClick={handleRightsIssueClick}
              disabled={uiBusy}
              className="bg-[#31415d] px-2 py-3 rounded text-white h-12 disabled:opacity-70"
            >
              Rights Issue
            </Button>
            <Button
              onClick={handleAllotManuallyClick}
              isLoading={isAllotManuallySubmitting}
              loadingText="Allotting..."
              disabled={uiBusy}
              className="bg-sflPrimary px-2 py-3 rounded text-white h-12 disabled:opacity-70"
            >
              Allot Manually
            </Button>
          </div>
        );
      }

      if (preEmptionRights === "waived") {
        return (
          <div className="flex gap-2">
            <Button
              onClick={handleAddSpecificShareholderClick}
              isLoading={isAddSpecificShareholderSubmitting}
              loadingText="Adding..."
              disabled={uiBusy}
              className="bg-sflPrimary px-2 py-3 rounded text-white h-12 disabled:opacity-70"
            >
              Add specific shareholder
            </Button>
          </div>
        );
      }
      return null;
    }

    // UnallotedShares
    if (isChangeTypeSelected("unallotedShares")) {
      if (!hasAnyAllot) {
        return (
          <Button
            onClick={handleAddSharesClick}
            disabled={uiBusy}
            className="bg-[#31415d] px-2 py-3 rounded text-white h-12 disabled:opacity-70"
          >
            Add shares on offer
          </Button>
        );
      }

      if (!preEmptionRights) return null;

      if (
        (rightsIssueProcessed || proRataProcessed) &&
        submittedAllocations.length > 0
      ) {
        return (
          <Button
            onClick={handleGoForward}
            className="bg-sflPrimary px-2 py-3 rounded text-white h-12"
          >
            Save and Proceed
          </Button>
        );
      }

      if (preEmptionRights === "applies") {
        return (
          <div className="flex gap-2">
            <Button
              onClick={handleAllocateSharesProRataClick}
              isLoading={isProRataSubmitting}
              loadingText="Allotting..."
              disabled={uiBusy}
              className="bg-[#31415d] px-2 py-3 rounded text-white h-12 disabled:opacity-70"
            >
              Allot Shares Pro Rata
            </Button>
            <Button
              onClick={handleRightsIssueClick}
              disabled={uiBusy}
              className="bg-[#31415d] px-2 py-3 rounded text-white h-12 disabled:opacity-70"
            >
              Rights Issue
            </Button>
            <Button
              onClick={handleAllotManuallyClick}
              isLoading={isAllotManuallySubmitting}
              loadingText="Allotting..."
              disabled={uiBusy}
              className="bg-sflPrimary px-2 py-3 rounded text-white h-12 disabled:opacity-70"
            >
              Allot Manually
            </Button>
          </div>
        );
      }

      if (preEmptionRights === "waived") {
        return (
          <div className="flex gap-2">
            <Button
              onClick={handleAddSpecificShareholderClick}
              isLoading={isAddSpecificShareholderSubmitting}
              loadingText="Adding..."
              disabled={uiBusy}
              className="bg-sflPrimary px-2 py-3 rounded text-white h-12 disabled:opacity-70"
            >
              Add specific shareholder
            </Button>
          </div>
        );
      }
      return null;
    }

    // PVTwithOneClass - Similar to unallotedShares but checks for proposed transactions
    if (
      isChangeTypeSelected("PVTwithOneClass") ||
      isChangeTypeSelected("existingAuth")
    ) {
      // Step 1: Show Add Shares button if no proposed allotment exists
      if (
        !getHasProposedTable("PVTwithOneClass") &&
        !getHasProposedTable("existingAuth")
      ) {
        return (
          <Button
            onClick={handleAddSharesClick}
            disabled={uiBusy}
            className="bg-[#31415d] px-2 py-3 rounded text-white h-12 disabled:opacity-70"
          >
            Add shares on offer
          </Button>
        );
      }

      // Step 2: After shares are added, show allocation options
      if (!preEmptionRights) return null;

      if (
        (rightsIssueProcessed || proRataProcessed) &&
        submittedAllocations.length > 0
      ) {
        return (
          <Button
            onClick={handleMoveForward}
            className="bg-sflPrimary px-2 py-3 rounded text-white h-12"
          >
            Save and Proceed
          </Button>
        );
      }

      if (preEmptionRights === "applies") {
        return (
          <div className="flex gap-2">
            <Button
              onClick={handleAllocateSharesProRataClick}
              isLoading={isProRataSubmitting}
              loadingText="Allotting..."
              disabled={uiBusy}
              className="bg-[#31415d] px-2 py-3 rounded text-white h-12 disabled:opacity-70"
            >
              Allot Shares Pro Rata
            </Button>
            <Button
              onClick={handleRightsIssueClick}
              disabled={uiBusy}
              className="bg-[#31415d] px-2 py-3 rounded text-white h-12 disabled:opacity-70"
            >
              Rights Issue
            </Button>
            <Button
              onClick={handleAllotManuallyClick}
              isLoading={isAllotManuallySubmitting}
              loadingText="Allotting..."
              disabled={uiBusy}
              className="bg-sflPrimary px-2 py-3 rounded text-white h-12 disabled:opacity-70"
            >
              Allot Manually
            </Button>
          </div>
        );
      }

      if (preEmptionRights === "waived") {
        return (
          <div className="flex gap-2">
            <Button
              onClick={handleAddSpecificShareholderClick}
              isLoading={isAddSpecificShareholderSubmitting}
              loadingText="Adding..."
              disabled={uiBusy}
              className="bg-sflPrimary px-2 py-3 rounded text-white h-12 disabled:opacity-70"
            >
              Add specific shareholder
            </Button>
          </div>
        );
      }
      return null;
    }

    // Share Transfer, Transmission, Reorg, and Other SAVE_AND_PROCEED_TYPES
    if (
      selectedChangeType.some((type) => SAVE_AND_PROCEED_TYPES.includes(type))
    ) {
      const hasReorg =
        selectedChangeType.includes("sharesConsolidation") ||
        selectedChangeType.includes("sharesSubdivision");

      if (pariChangeTypes.length > 0) {
        return (
          <Button
            onClick={handleGoForward}
            className="bg-sflPrimary px-2 py-3 rounded text-white h-12"
          >
            Save and Proceed
          </Button>
        );
      }

      // For share consolidation / subdivision, do NOT show the primary
      // "Add shares on offer" button until there is a proposed table.
      if (hasReorg) {
        return null;
      }

      return (
        <Button
          onClick={handleAddSharesClick}
          disabled={uiBusy}
          className="bg-[#31415d] px-2 py-3 rounded text-white h-12 disabled:opacity-70"
        >
          Add shares on offer
        </Button>
      );
    }

    return (
      <Button
        onClick={handleSaveAndProceed}
        className="bg-sflPrimary px-2 py-3 rounded text-white h-12"
      >
        Save and Proceed
      </Button>
    );
  };

  // Compute pariChangeTypes using current store (no extra fetch here)
  const getHasProposedTable = (changeType) => {
    if (changeType === "authorization") {
      return hasProposedAuthorizations;
    }
    const targetChangeType = processTypeToChangeType[changeType];
    return initiatedTransactions.items.some(
      (tx) =>
        tx.transactionType === Constants.TRANSACTION_TYPES.MANUAL &&
        tx.status !== Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.PROCESSED &&
        tx.changeType === targetChangeType &&
        tx.processType === changeType &&
        ((Array.isArray(tx.allotments) && tx.allotments.length > 0) ||
          (Array.isArray(tx.allotmentsPopulated) &&
            tx.allotmentsPopulated.length > 0) ||
          (Array.isArray(tx.shareCapitalChanges) &&
            tx.shareCapitalChanges.length > 0))
    );
  };

  const pariChangeTypes = useMemo(
    () => selectedChangeType.filter((type) => getHasProposedTable(type)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedChangeType,
      initiatedTransactions.items,
      hasProposedAuthorizations,
      isBootstrapping,
      isSyncingAfterUpdate,
    ]
  );

  return (
    <>
      <ErrorBanner errors={formStepErrors} className="mb-2" />

      <div data-label="Share Capital Information" className="df-example">
        <div className="pb-4 p-1-0 p-r-0">
          {/* Comments */}
          {shareInformationComments.length > 0 && (
            <div className="px-8 pt-5 pb-6 mb-4 bg-white">
              <CommentsList
                comments={shareInformationComments}
                color="text-red-500"
              />
              <p className="mt-6 text-sm text-primary">
                Comments: {shareInformationComments.length} of{" "}
                {shareInformationComments.length}
              </p>
            </div>
          )}

          {/* Summary with graceful loading */}
          {shares.length > 0 ? (
            <div className="relative">
              {isBootstrapping && <LoadingOverlay label="Loading summary…" />}
              <ShareInformationTopSection
                sectionNumber={isInChangesModule ? "" : "3"}
                sumNominalCapital={shares
                  .map((share) => share.sharesValue * share.sharesNumber)
                  .reduce((a, b) => a + b, 0)}
                sumOfVotingRights={shares.reduce(
                  (acc, share) => roundNumber(acc + Number(share.votingRights)),
                  0
                )}
              />
            </div>
          ) : showAddMoreSharesBtn ? (
            <div className="w-2/3 space-y-2">
              <div className="inline-flex px-6 py-3 bg-white rounded shadow-sm">
                <p className="text-gray-800">
                  We have no current share capital information to allocate.
                  Please click the{" "}
                  <b className="text-secondary">+ Add class of shares</b> to add
                  nominal capital.
                </p>
              </div>
            </div>
          ) : null}

          {/* Nominal Shares Table */}
          {showAddMoreSharesBtn && shares.length > 0 && (
            <div className="relative">
              {(isBootstrapping || isSyncingAfterUpdate) && (
                <LoadingOverlay label="Refreshing shares…" />
              )}
              <SharesTable
                handleEdit={handleEdit}
                changeType={primaryChangeType}
                setMode={setMode}
                companyId={companyId}
                filteredOfficials={filteredOfficials}
                mode={mode}
                takenShares={takenShares}
                authorizations={shareAuthorizationSlice?.shares || []}
                allotments={shareAllotments}
                handleView={handleView}
                nominalShares={shares}
                showActionColumn={
                  Cookies.get("role") !== Constants.COOKIE_ROLES.OFFICIAL
                }
                showAddCommentColumn={props?.showAddCommentColumn || false}
                comments={props?.shareInformationComments || []}
                isInChangesModule={isInChangesModule}
                isInManualUpdate={isInManualUpdate}
              />
            </div>
          )}

          {!showAddMoreSharesBtn ? (
            <div className="relative">
              {isBootstrapping && <LoadingOverlay label="Loading form…" />}
              <ShareSelection
                setShowAddMoreSharesBtn={setShowAddMoreSharesBtn}
                mode={mode}
                takenShares={takenShares}
                sharesCategory=""
                nominalShares={shares}
                isInChangesModule={isInChangesModule}
                isInManualUpdate={isInManualUpdate}
                changeType={primaryChangeType}
              />
            </div>
          ) : (
            role !== Constants.COOKIE_ROLES.OFFICIAL &&
            // Hide add-class button whenever any of these change types
            // are active in the current Share Capital change workflow
            !selectedChangeType.some((type) =>
              [
                "shareTransfer",
                "forfeitureOfShares",
                "transmissionOfShares",
                "surrenderOfShares",
                "sharesBuyback",
                "cancelationOfTreasuryShares",
                "saleTransferOfTreasuryShares",
                "sharesRedemption",
                "noticeToCourt",
                "takeoverOffer",
                "noticeOfChangeOfName",
                "noticeOfParticulars",
                "solvencyStatement",
                "courtOrder",
                "sharesConsolidation",
                "sharesSubdivision",
              ].includes(type)
            ) && (
              <Button
                variant="text"
                disabled={role === Constants.COOKIE_ROLES.OFFICIAL || uiBusy}
                onClick={handleAddClassOfShares}
                className="mt-4 disabled:opacity-60"
              >
                <PlusIcon className="w-6 h-6" aria-hidden="true" />
                <span>
                  {shares.length > 0
                    ? "Add new class of shares"
                    : "Add class of shares"}
                </span>
              </Button>
            )
          )}

          {/* Proposed Sections */}
          {(isInChangesModule || isInManualUpdate) && (
            <>
              {/* Proposed Authorization Table */}
              {isChangeTypeSelected("authorization") && (
                <div className="relative">
                  {(isBootstrapping || isSyncingAfterUpdate) && (
                    <LoadingOverlay label="Loading proposed authorizations…" />
                  )}
                  <ProposedSharesTable
                    changeType="authorization"
                    handleEdit={handleEdit}
                    setMode={setMode}
                    allotments={shareAllotments}
                    companyId={companyId}
                    mode={mode}
                    takenShares={takenShares}
                    handleView={handleView}
                    authorizations={shareAuthorizationSlice?.shares || []}
                    filteredOfficials={filteredOfficials}
                    showActionColumn={role !== Constants.COOKIE_ROLES.OFFICIAL}
                    showAddCommentColumn={props?.showAddCommentColumn || false}
                    comments={props?.shareInformationComments || []}
                    isInChangesModule={isInChangesModule}
                    isInManualUpdate={isInManualUpdate}
                    refreshTrigger={refreshTrigger}
                    onSave={handleSaveReorganization}
                  />
                </div>
              )}

              {/* Proposed Allotment/Change Tables */}
              {selectedChangeType
                .filter((type) => type !== "authorization")
                .map((changeType) => (
                  <div className="relative" key={changeType}>
                    {(isBootstrapping || isSyncingAfterUpdate) && (
                      <LoadingOverlay label="Loading proposed changes…" />
                    )}
                    <ProposedSharesTable
                      changeType={changeType}
                      handleEdit={handleEdit}
                      setMode={setMode}
                      allotments={shareAllotments}
                      companyId={companyId}
                      mode={mode}
                      takenShares={takenShares}
                      filteredOfficials={filteredOfficials}
                      onRefresh={() => setRefreshTrigger((prev) => prev + 1)}
                      handleView={handleView}
                      authorizations={shareAuthorizationSlice?.shares || []}
                      showActionColumn={
                        role !== Constants.COOKIE_ROLES.OFFICIAL
                      }
                      showAddCommentColumn={
                        props?.showAddCommentColumn || false
                      }
                      comments={props?.shareInformationComments || []}
                      isInChangesModule={isInChangesModule}
                      isInManualUpdate={isInManualUpdate}
                      refreshTrigger={refreshTrigger}
                      createdTransactionsIds={createdTransactionsIds}
                      onAddAnotherShareOnOffer={(unproposedClasses) =>
                        handleAddAnotherShareOnOffer(
                          unproposedClasses,
                          changeType
                        )
                      }
                      showAdditionalSharesForm={showAdditionalSharesForm}
                      activeAdditionalFormChangeType={
                        activeAdditionalFormChangeType
                      }
                      isSyncingAfterUpdate={isSyncingAfterUpdate}
                      additionalSharesFormComponent={
                        showAdditionalSharesForm &&
                        activeAdditionalFormChangeType === changeType ? (
                          <AddSharesOnOffer
                            key={`additional-${changeType}`}
                            allChangeTypes={[changeType]}
                            filteredOfficials={filteredOfficials}
                            onSubmit={() => {
                              hideAdditionalSharesForm();
                              setRefreshTrigger((prev) => prev + 1);
                            }}
                            onCancel={hideAdditionalSharesForm}
                            initialData={{}}
                            companyId={companyId}
                            excludedClasses={excludedShareClasses}
                          />
                        ) : null
                      }
                    />
                  </div>
                ))}

              {/* DateOfAuthorization */}
              {(isChangeTypeSelected("authorization") ||
                isChangeTypeSelected("privateWithMoreThanOneClass")) &&
                (hasAnyAuth || hasAnyAllot) && (
                  <div className="relative">
                    {isBootstrapping && (
                      <LoadingOverlay label="Loading dates…" />
                    )}
                    <DateOfAuthorization
                      onDateChange={setDateFields}
                      onDataCheck={() => {}}
                      isInManualUpdate={isInManualUpdate}
                    />
                  </div>
                )}

              {pendingChangeTypes.length > 0 &&
                (() => {
                  const currentChangeType =
                    pendingChangeTypes[currentFormIndex];
                  return (
                    <div className="relative">
                      {isSyncingAfterUpdate && (
                        <LoadingOverlay label="Saving changes…" />
                      )}
                      <AddSharesOnOffer
                        key={`additional-${currentChangeType}`}
                        changeType={currentChangeType}
                        allChangeTypes={pendingChangeTypes}
                        filteredOfficials={filteredOfficials}
                        isLastForm={
                          currentFormIndex === pendingChangeTypes.length - 1
                        }
                        onCancel={hideAddSharesForm}
                        onSubmit={() => {
                          setRefreshTrigger((prev) => prev + 1);
                          if (
                            currentFormIndex <
                            pendingChangeTypes.length - 1
                          ) {
                            setCurrentFormIndex((i) => i + 1);
                          } else {
                            setPendingChangeTypes([]);
                            setCurrentFormIndex(0);
                          }
                        }}
                        initialData={{}}
                        companyId={companyId}
                        transactionId={
                          currentChangeType === "privateWithMoreThanOneClass"
                            ? transactionId
                            : null
                        }
                      />
                    </div>
                  );
                })()}

              {/* Pre-emption Rights */}
              {(isChangeTypeSelected("privateWithMoreThanOneClass") ||
                isChangeTypeSelected("PVTwithOneClass") ||
                isChangeTypeSelected("unallotedShares") ||
                isChangeTypeSelected("existingAuth")) &&
                (getHasProposedTable("privateWithMoreThanOneClass") ||
                  getHasProposedTable("PVTwithOneClass") ||
                  getHasProposedTable("unallotedShares") ||
                  getHasProposedTable("existingAuth")) && (
                  <div className="relative">
                    {(isBootstrapping || isSyncingAfterUpdate) && (
                      <LoadingOverlay label="Loading pre-emption…" />
                    )}
                    <PreEmptionRights
                      onSubmit={(v) => dispatch(setPreEmptionRights(v))}
                    />
                  </div>
                )}

              {showAllotmentForm && allotmentMode === "rightsIssue" && (
                <div className="relative mt-4">
                  {(isBootstrapping || isSyncingAfterUpdate) && (
                    <LoadingOverlay label="Loading rights issue…" />
                  )}
                  <AllotmentForm
                    shareAllotments={(() => {
                      try {
                        let processType = "unallotedShares";
                        if (
                          selectedChangeType.includes(
                            "privateWithMoreThanOneClass"
                          )
                        )
                          processType = "privateWithMoreThanOneClass";
                        else if (selectedChangeType.includes("PVTwithOneClass"))
                          processType = "PVTwithOneClass";
                        else if (selectedChangeType.includes("existingAuth"))
                          processType = "existingAuth";

                        const allAvailableShares =
                          getTransactionAllotmentData(processType) ||
                          shareAllotments ||
                          [];

                        if (
                          submittedAllocations &&
                          submittedAllocations.length > 0
                        ) {
                          const processedShareKeys = new Set(
                            submittedAllocations
                              .filter(
                                (allocation) =>
                                  allocation &&
                                  allocation.classOfShares &&
                                  allocation.sharesOnOffer !== undefined
                              )
                              .map(
                                (allocation) =>
                                  `${allocation.classOfShares}#${Number(
                                    allocation.sharesOnOffer
                                  )}`
                              )
                          );

                          const remainingShares = allAvailableShares.filter(
                            (share) => {
                              if (
                                !share ||
                                !share.classOfShares ||
                                share.sharesOnOffer === undefined
                              ) {
                                return false;
                              }
                              const shareKey = `${share.classOfShares}#${Number(
                                share.sharesOnOffer
                              )}`;
                              return !processedShareKeys.has(shareKey);
                            }
                          );

                          return remainingShares;
                        }

                        return Array.isArray(allAvailableShares)
                          ? allAvailableShares
                          : [];
                      } catch (error) {
                        console.error(
                          "Error filtering shareAllotments:",
                          error
                        );
                        return [];
                      }
                    })()}
                    mode={allotmentMode}
                    submittedAllocations={submittedAllocations}
                    onUpdateAllocations={setSubmittedAllocations}
                    onCancel={() => {
                      setShowAllotmentForm(false);
                      setIsOpeningForNextClass(false);
                      if (submittedAllocations.length === 0) {
                        setRightsIssueProcessed(false);
                        setProRataProcessed(false);
                      }
                    }}
                    onSubmit={updateRightsIssueTransaction}
                    companyId={companyId}
                  />
                </div>
              )}

              {showAllotmentForm && allotmentMode === "proRata" && (
                <div className="relative mt-4">
                  {(isBootstrapping || isSyncingAfterUpdate) && (
                    <LoadingOverlay label="Loading pro rata…" />
                  )}
                  <AllotmentForm
                    shareAllotments={(() => {
                      try {
                        let processType = "unallotedShares";
                        if (
                          selectedChangeType.includes(
                            "privateWithMoreThanOneClass"
                          )
                        )
                          processType = "privateWithMoreThanOneClass";
                        else if (selectedChangeType.includes("PVTwithOneClass"))
                          processType = "PVTwithOneClass";
                        else if (selectedChangeType.includes("existingAuth"))
                          processType = "existingAuth";

                        const allAvailableShares =
                          getTransactionAllotmentData(processType) ||
                          shareAllotments ||
                          [];

                        if (
                          submittedAllocations &&
                          submittedAllocations.length > 0
                        ) {
                          const processedShareKeys = new Set(
                            submittedAllocations
                              .filter(
                                (allocation) =>
                                  allocation &&
                                  allocation.classOfShares &&
                                  allocation.sharesOnOffer !== undefined
                              )
                              .map(
                                (allocation) =>
                                  `${allocation.classOfShares}#${Number(
                                    allocation.sharesOnOffer
                                  )}`
                              )
                          );

                          const remainingShares = allAvailableShares.filter(
                            (share) => {
                              if (
                                !share ||
                                !share.classOfShares ||
                                share.sharesOnOffer === undefined
                              ) {
                                return false;
                              }
                              const shareKey = `${share.classOfShares}#${Number(
                                share.sharesOnOffer
                              )}`;
                              return !processedShareKeys.has(shareKey);
                            }
                          );

                          return remainingShares;
                        }

                        return Array.isArray(allAvailableShares)
                          ? allAvailableShares
                          : [];
                      } catch (error) {
                        console.error(
                          "Error filtering shareAllotments:",
                          error
                        );
                        return [];
                      }
                    })()}
                    mode={allotmentMode}
                    submittedAllocations={submittedAllocations}
                    onUpdateAllocations={setSubmittedAllocations}
                    onCancel={() => {
                      setShowAllotmentForm(false);
                      setIsOpeningForNextClass(false);
                      if (submittedAllocations.length === 0) {
                        setRightsIssueProcessed(false);
                        setProRataProcessed(false);
                      }
                    }}
                    onSubmit={updateProRataTransaction}
                    companyId={companyId}
                  />
                </div>
              )}

              {/* Show results table when allocations exist - but not for manual or specificShareholderOffer modes */}
              {Array.isArray(submittedAllocations) &&
                submittedAllocations.length > 0 &&
                !showAllotmentForm &&
                allotmentMode &&
                allotmentMode !== "manual" &&
                allotmentMode !== "specificShareholderOffer" &&
                allotmentMode !== "" &&
                true && (
                  <div className="relative">
                    <AllotmentResultsTable
                      allocations={submittedAllocations}
                      mode={allotmentMode}
                      isEditable={true}
                      availableShares={(() => {
                        // Get available shares for current process type
                        let processType = "unallotedShares";
                        if (
                          selectedChangeType.includes(
                            "privateWithMoreThanOneClass"
                          )
                        )
                          processType = "privateWithMoreThanOneClass";
                        else if (selectedChangeType.includes("PVTwithOneClass"))
                          processType = "PVTwithOneClass";
                        else if (selectedChangeType.includes("existingAuth"))
                          processType = "existingAuth";
                        return getTransactionAllotmentData(processType) || [];
                      })()}
                      onAddAnotherClass={handleAddAnotherAllotmentClass}
                      onDelete={async (index) => {
                        const row = submittedAllocations[index];
                        const isLastItem = submittedAllocations.length === 1;

                        try {
                          await deleteAllocationFromTransaction({
                            classOfShares: row.classOfShares,
                            sharesOnOffer: row.sharesOnOffer,
                            option: row.option,
                          });

                          // If this was the last item, reset the mode states
                          if (isLastItem) {
                            setRightsIssueProcessed(false);
                            setProRataProcessed(false);
                            setAllotmentMode(""); // Clear allotment mode
                            // Note: submittedAllocations will be updated by deleteAllocationFromTransaction
                          }
                        } catch (error) {
                          console.error("Error deleting allocation:", error);
                        }
                      }}
                      onUpdate={(index, updatedAllocation) => {
                        const updatedAllocations = [...submittedAllocations];
                        updatedAllocations[index] = updatedAllocation;
                        setSubmittedAllocations(updatedAllocations);
                      }}
                      onSubmit={
                        allotmentMode === "rightsIssue"
                          ? updateRightsIssueTransaction
                          : updateProRataTransaction
                      }
                      companyId={companyId}
                    />
                  </div>
                )}
            </>
          )}

          {/* AddSharesOnOffer Wizard or ShareSelection */}
        </div>
      </div>

      <hr />

      {/* Tabs */}
      {shares.length > 0 && (
        <div className="relative">
          {isBootstrapping && <LoadingOverlay label="Preparing tabs…" />}
          <SharesCategoryTabs
            disabledRightsEditor={role === Constants.COOKIE_ROLES.OFFICIAL}
            disableTabs={!showAddMoreSharesBtn || uiBusy}
            isInChangesModule={isInChangesModule}
            isInManualUpdate={isInManualUpdate}
            nominalShares={shares}
          />
        </div>
      )}

      {/* Footer buttons */}
      {isInManualUpdate ? (
        <div className="flex justify-between py-4">
          <button
            type="button"
            onClick={handleGoBack}
            disabled={uiBusy}
            className="flex items-start float-right font-semibold rounded text-primary mt-2 disabled:opacity-60"
          >
            <span className="flex items-center mr-4 h-7">
              <ChevronLeftIcon
                className="flex-shrink-0 w-5 h-5"
                aria-hidden="true"
              />
            </span>
            <p className="font-semibold">Go Back</p>
          </button>
          {renderBottomButtons()}
        </div>
      ) : (
        !isInChangesModule && (
          <div className="flex justify-between py-3">
            <Button
              onClick={handleBackBtn}
              disabled={uiBusy}
              className="bg-gray-600 hover:bg-gray-700 disabled:opacity-60"
            >
              Go back
            </Button>
            <Button
              type="submit"
              isLoading={isSubmitting || uiBusy}
              onClick={handleSaveAndProceed}
              className="bg-green-600 hover:bg-green-700"
            >
              {role === Constants.COOKIE_ROLES.OFFICIAL
                ? "Next step"
                : submitBtnLabel}
            </Button>
          </div>
        )
      )}

      {/* Modals */}
      <ModalWrapper
        name="main_shares_modal_view"
        title="Share Capital Information"
      >
        <ViewShare selectedShare={{}} />
      </ModalWrapper>

      <ModalWrapper
        name="main_shares_modal_edit"
        title="Edit Share Capital Information"
        maxWidth="sm:max-w-[45rem]"
      >
        <ShareSelection
          setShowAddMoreSharesBtn={setShowAddMoreSharesBtn}
          mode={mode}
          takenShares={takenShares}
          sharesCategory=""
          isInChangesModule={isInChangesModule}
          isInManualUpdate={isInManualUpdate}
          changeType={primaryChangeType}
        />
      </ModalWrapper>
    </>
  );
});

export default NominalShareCapitalTab;
